import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { MetadataList } from '../../shared/detail/MetadataList';
import { partnerCreditRepositoryToken } from './repository';
import type {
  PartnerCreditApproval,
  PartnerCreditConfiguration,
  PartnerCreditPreview,
} from './types';

interface Values {
  readonly name: string;
  readonly creditEndpoint: string;
  readonly statusEndpoint: string;
  readonly healthEndpoint: string;
  readonly transactionResultEndpoint: string;
  readonly authType: 'HMAC_API_KEY' | 'OAUTH2_CLIENT_CREDENTIALS';
  readonly hmacKeyId: string;
  readonly oauthTokenEndpoint: string;
  readonly oauthClientId: string;
  readonly oauthScopes: string;
  readonly oauthAudience: string;
  readonly timeoutMilliseconds: number;
  readonly maximumAttempts: number;
  readonly initialDelaySeconds: number;
  readonly maximumDelaySeconds: number;
  readonly multiplier: number;
  readonly jitterPercent: number;
  readonly secret: string;
  readonly reason: string;
}

const defaults: Values = {
  name: '',
  creditEndpoint: '',
  statusEndpoint: '',
  healthEndpoint: '',
  transactionResultEndpoint: '',
  authType: 'HMAC_API_KEY',
  hmacKeyId: '',
  oauthTokenEndpoint: '',
  oauthClientId: '',
  oauthScopes: '',
  oauthAudience: '',
  timeoutMilliseconds: 5000,
  maximumAttempts: 5,
  initialDelaySeconds: 2,
  maximumDelaySeconds: 60,
  multiplier: 2,
  jitterPercent: 20,
  secret: '',
  reason: '',
};

export function PartnerCreditWizard({
  dataSource,
  currentConfiguration,
  onClose,
  onPublished,
  open,
  partnerId,
}: {
  readonly dataSource: DataSource;
  readonly currentConfiguration: PartnerCreditConfiguration;
  readonly onClose: () => void;
  readonly onPublished: (approval: PartnerCreditApproval) => void;
  readonly open: boolean;
  readonly partnerId: string;
}) {
  const repository = useRepository(partnerCreditRepositoryToken);
  const { t } = useTranslation();
  const [preview, setPreview] = useState<PartnerCreditPreview | null>(null);
  const [previewRevision, setPreviewRevision] = useState<number | null>(null);
  const [approval, setApproval] = useState<PartnerCreditApproval | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'publish' | null>(null);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const revisionRef = useRef(0);
  const previewingRef = useRef(false);
  const publishingRef = useRef(false);
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<Values>({ defaultValues: defaults });
  const authType = watch('authType');

  useEffect(() => {
    if (!open) return;
    reset(valuesFrom(currentConfiguration));
    setPreview(null);
    setPreviewRevision(null);
    setApproval(null);
    setConfirmed(false);
    setBusy(null);
    setError(null);
    revisionRef.current = 0;
    previewingRef.current = false;
    publishingRef.current = false;
    idempotencyKeyRef.current = crypto.randomUUID();
  }, [currentConfiguration, open, reset]);

  const invalidate = () => {
    revisionRef.current += 1;
    setPreview(null);
    setPreviewRevision(null);
    setApproval(null);
    setConfirmed(false);
    setError(null);
    idempotencyKeyRef.current = crypto.randomUUID();
  };
  const requestPreview = handleSubmit(async (values) => {
    if (previewingRef.current || busy !== null) return;
    previewingRef.current = true;
    setBusy('preview');
    setError(null);
    try {
      const result = await repository.preview(partnerId, normalize(values));
      setPreview(result);
      setPreviewRevision(revisionRef.current);
      setConfirmed(false);
    } catch (cause) {
      setError(safeError(cause, t('partnerCredit.errors.preview')));
    } finally {
      previewingRef.current = false;
      setBusy(null);
    }
  });
  const publish = async () => {
    if (
      preview === null ||
      previewRevision !== revisionRef.current ||
      !confirmed ||
      publishingRef.current ||
      busy !== null
    )
      return;
    publishingRef.current = true;
    setBusy('publish');
    setError(null);
    try {
      const result = await repository.createVersion(
        partnerId,
        { ...normalize(getValues()), previewHash: preview.previewHash },
        idempotencyKeyRef.current,
      );
      setApproval(result.data);
      reset({ ...getValues(), secret: '' });
      idempotencyKeyRef.current = crypto.randomUUID();
      onPublished(result.data);
    } catch (cause) {
      setError(safeError(cause, t('partnerCredit.errors.publish')));
      publishingRef.current = false;
    } finally {
      setBusy(null);
    }
  };
  const close = () => {
    if (busy !== null) return;
    reset(defaults);
    setConfirmed(false);
    onClose();
  };
  const fresh = preview !== null && previewRevision === revisionRef.current;

  return (
    <Dialog
      aria-describedby="partner-credit-wizard-description"
      fullWidth
      maxWidth="md"
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('partnerCredit.wizard.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack
            component="form"
            id="partner-credit-preview-form"
            noValidate
            onChange={invalidate}
            onSubmit={requestPreview}
            spacing={2}
          >
            <DataSourceBadge source={dataSource} />
            <DialogContentText id="partner-credit-wizard-description">
              {t('partnerCredit.wizard.description')}
            </DialogContentText>
            <Alert severity="warning">{t('partnerCredit.wizard.secretNotice')}</Alert>
            {error === null ? null : <SafeMutationError error={error} />}
            {approval === null ? null : (
              <Alert severity="success">
                {t('partnerCredit.wizard.pendingApproval', { id: approval.id })}
              </Alert>
            )}
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.name !== undefined}
              label={t('partnerCredit.fields.name')}
              required
              slotProps={{ htmlInput: { maxLength: 200 } }}
              {...register('name', {
                validate: (value) => isTrimmedLength(value, 1, 200),
              })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.creditEndpoint !== undefined}
              label={t('partnerCredit.fields.creditEndpoint')}
              required
              type="url"
              {...register('creditEndpoint', { validate: isHttpsUri })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.statusEndpoint !== undefined}
              label={t('partnerCredit.fields.statusEndpoint')}
              required
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              {...register('statusEndpoint', {
                validate: (value) => isTrimmedLength(value, 1, 2000),
              })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.healthEndpoint !== undefined}
              label={t('partnerCredit.fields.healthEndpoint')}
              required
              type="url"
              {...register('healthEndpoint', { validate: isHttpsUri })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.transactionResultEndpoint !== undefined}
              helperText={t('partnerCredit.fields.transactionResultEndpointHelp')}
              label={t('partnerCredit.fields.transactionResultEndpoint')}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              type="url"
              {...register('transactionResultEndpoint', {
                validate: (value) => value.trim().length === 0 || isHttpsUri(value),
              })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              label={t('partnerCredit.fields.authType')}
              name="authType"
              onChange={(event) =>
                setValue('authType', event.target.value as Values['authType'], {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              select
              value={authType ?? 'HMAC_API_KEY'}
            >
              <MenuItem value="HMAC_API_KEY">HMAC API key</MenuItem>
              <MenuItem value="OAUTH2_CLIENT_CREDENTIALS">OAuth 2 client credentials</MenuItem>
            </TextField>
            {authType === 'HMAC_API_KEY' ? (
              <TextField
                disabled={busy !== null || approval !== null}
                error={errors.hmacKeyId !== undefined}
                label={t('partnerCredit.fields.keyReference')}
                required
                slotProps={{ htmlInput: { maxLength: 200 } }}
                {...register('hmacKeyId', {
                  validate: (value) => isTrimmedLength(value, 1, 200),
                })}
              />
            ) : (
              <>
                <TextField
                  disabled={busy !== null || approval !== null}
                  error={errors.oauthTokenEndpoint !== undefined}
                  label={t('partnerCredit.fields.oauthTokenEndpoint')}
                  required
                  type="url"
                  {...register('oauthTokenEndpoint', { validate: isHttpsUri })}
                />
                <TextField
                  disabled={busy !== null || approval !== null}
                  error={errors.oauthClientId !== undefined}
                  label={t('partnerCredit.fields.oauthClientId')}
                  required
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                  {...register('oauthClientId', {
                    validate: (value) => isTrimmedLength(value, 1, 500),
                  })}
                />
                <TextField
                  disabled={busy !== null || approval !== null}
                  error={errors.oauthScopes !== undefined}
                  label={t('partnerCredit.fields.oauthScopes')}
                  {...register('oauthScopes', { validate: isValidOAuthScopes })}
                />
                <TextField
                  disabled={busy !== null || approval !== null}
                  error={errors.oauthAudience !== undefined}
                  label={t('partnerCredit.fields.oauthAudience')}
                  slotProps={{ htmlInput: { maxLength: 1000 } }}
                  {...register('oauthAudience', {
                    validate: (value) =>
                      value.trim().length === 0 || isTrimmedLength(value, 1, 1000),
                  })}
                />
              </>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.timeoutMilliseconds !== undefined}
                integer
                label={t('partnerCredit.fields.timeout')}
                registration={register('timeoutMilliseconds', {
                  validate: (value) => isIntegerInRange(value, 100, 30000),
                  valueAsNumber: true,
                })}
              />
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.maximumAttempts !== undefined}
                integer
                label={t('partnerCredit.fields.maximumAttempts')}
                registration={register('maximumAttempts', {
                  validate: (value) => isIntegerInRange(value, 1, 20),
                  valueAsNumber: true,
                })}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.initialDelaySeconds !== undefined}
                integer
                label={t('partnerCredit.fields.initialDelay')}
                registration={register('initialDelaySeconds', {
                  validate: (value) => isIntegerInRange(value, 1, 3600),
                  valueAsNumber: true,
                })}
              />
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.maximumDelaySeconds !== undefined}
                integer
                label={t('partnerCredit.fields.maximumDelay')}
                registration={register('maximumDelaySeconds', {
                  validate: (value) => isIntegerInRange(value, 1, 86400),
                  valueAsNumber: true,
                })}
              />
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.multiplier !== undefined}
                label={t('partnerCredit.fields.multiplier')}
                registration={register('multiplier', {
                  validate: (value) => isFiniteInRange(value, 1, 10),
                  valueAsNumber: true,
                })}
              />
              <NumberInput
                disabled={busy !== null || approval !== null}
                error={errors.jitterPercent !== undefined}
                label={t('partnerCredit.fields.jitter')}
                registration={register('jitterPercent', {
                  validate: (value) => isFiniteInRange(value, 0, 100),
                  valueAsNumber: true,
                })}
              />
            </Stack>
            <TextField
              autoComplete="new-password"
              disabled={busy !== null || approval !== null}
              error={errors.secret !== undefined}
              label={t('partnerCredit.fields.secret')}
              required
              slotProps={{ htmlInput: { maxLength: 4096, minLength: 32 } }}
              type="password"
              {...register('secret', {
                validate: (value) =>
                  value.trim().length > 0 && value.length >= 32 && value.length <= 4096,
              })}
            />
            <TextField
              disabled={busy !== null || approval !== null}
              error={errors.reason !== undefined}
              label={t('partnerCredit.fields.reason')}
              minRows={2}
              multiline
              required
              slotProps={{ htmlInput: { maxLength: 2000 } }}
              {...register('reason', {
                validate: (value) => isTrimmedLength(value, 1, 2000),
              })}
            />
          </Stack>
          {fresh && preview !== null ? (
            <Paper
              aria-label={t('partnerCredit.wizard.previewTitle')}
              component="section"
              sx={{ p: 2 }}
              variant="outlined"
            >
              <Stack spacing={2}>
                <Typography component="h3" variant="subtitle1">
                  {t('partnerCredit.wizard.previewTitle')}
                </Typography>
                <MetadataList
                  items={[
                    {
                      label: t('partnerCredit.wizard.targetPartner'),
                      value: partnerId,
                    },
                    {
                      label: t('partnerCredit.wizard.currentImpact'),
                      value:
                        currentConfiguration.currentVersion === null
                          ? t('partnerCredit.notConfigured')
                          : `v${currentConfiguration.currentVersion.version} · ${currentConfiguration.status ?? t('common.notAvailable')}`,
                    },
                    {
                      label: t('partnerCredit.wizard.proposedImpact'),
                      value: t('partnerCredit.wizard.pendingImpact'),
                    },
                  ]}
                />
                <Typography component="h4" variant="subtitle2">
                  {t('partnerCredit.sections.publicConfiguration')}
                </Typography>
                <MetadataList items={publicPreviewItems(preview, t)} />
                <Alert severity="info">{t('partnerCredit.wizard.certificationRequired')}</Alert>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      disabled={busy !== null}
                      onChange={(event) => setConfirmed(event.target.checked)}
                      required
                    />
                  }
                  label={t('partnerCredit.wizard.confirmPublish')}
                />
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy !== null} onClick={close}>
          {approval === null ? t('actions.cancel') : t('actions.close')}
        </Button>
        {approval === null ? (
          <>
            <Button
              disabled={busy !== null}
              form="partner-credit-preview-form"
              loading={busy === 'preview'}
              type="submit"
            >
              {t('partnerCredit.actions.preview')}
            </Button>
            <Button
              disabled={!fresh || !confirmed || busy !== null}
              loading={busy === 'publish'}
              onClick={(event) => {
                if (event.detail > 1) return;
                void publish();
              }}
              variant="contained"
            >
              {t('partnerCredit.actions.requestApproval')}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function NumberInput({
  disabled,
  error,
  integer = false,
  label,
  registration,
}: {
  readonly disabled: boolean;
  readonly error: boolean;
  readonly integer?: boolean;
  readonly label: string;
  readonly registration: UseFormRegisterReturn;
}) {
  return (
    <TextField
      disabled={disabled}
      error={error}
      label={label}
      slotProps={{ htmlInput: { step: integer ? 1 : 'any' } }}
      type="number"
      {...registration}
    />
  );
}

function publicPreviewItems(preview: PartnerCreditPreview, t: TFunction) {
  const configuration = preview.configuration;
  const authItems =
    configuration.authType === 'HMAC_API_KEY'
      ? [
          {
            label: t('partnerCredit.fields.keyReference'),
            value: configuration.hmacKeyId ?? t('common.notAvailable'),
          },
        ]
      : [
          {
            label: t('partnerCredit.fields.oauthTokenEndpoint'),
            value: configuration.oauthTokenEndpoint ?? t('common.notAvailable'),
          },
          {
            label: t('partnerCredit.fields.oauthClientId'),
            value: configuration.oauthClientId ?? t('common.notAvailable'),
          },
          {
            label: t('partnerCredit.fields.oauthScopes'),
            value: configuration.oauthScopes?.join(' ') ?? t('common.notAvailable'),
          },
          {
            label: t('partnerCredit.fields.oauthAudience'),
            value: configuration.oauthAudience ?? t('common.notAvailable'),
          },
        ];
  return [
    { label: t('partnerCredit.fields.name'), value: configuration.name },
    { label: t('partnerCredit.fields.creditEndpoint'), value: configuration.creditEndpoint },
    { label: t('partnerCredit.fields.statusEndpoint'), value: configuration.statusEndpoint },
    { label: t('partnerCredit.fields.healthEndpoint'), value: configuration.healthEndpoint },
    {
      label: t('partnerCredit.fields.transactionResultEndpoint'),
      value: configuration.transactionResultEndpoint ?? t('common.notAvailable'),
    },
    { label: t('partnerCredit.fields.authType'), value: configuration.authType },
    ...authItems,
    {
      label: t('partnerCredit.fields.timeout'),
      value: configuration.timeoutMilliseconds,
    },
    {
      label: t('partnerCredit.fields.maximumAttempts'),
      value: configuration.retryPolicy.maximumAttempts,
    },
    {
      label: t('partnerCredit.fields.initialDelay'),
      value: configuration.retryPolicy.initialDelaySeconds,
    },
    {
      label: t('partnerCredit.fields.maximumDelay'),
      value: configuration.retryPolicy.maximumDelaySeconds,
    },
    {
      label: t('partnerCredit.fields.multiplier'),
      value: configuration.retryPolicy.multiplier,
    },
    {
      label: t('partnerCredit.fields.jitter'),
      value: configuration.retryPolicy.jitterPercent,
    },
    { label: t('partnerCredit.fields.previewHash'), value: preview.previewHash },
  ];
}

function isTrimmedLength(value: string, minimum: number, maximum: number) {
  const length = value.trim().length;
  return length >= minimum && length <= maximum;
}

function isHttpsUri(value: string) {
  const normalized = value.trim();
  if (!/^https:\/\//u.test(normalized) || /^https:\/\/\//u.test(normalized)) return false;
  try {
    return new URL(normalized).protocol === 'https:';
  } catch {
    return false;
  }
}

function isIntegerInRange(value: number, minimum: number, maximum: number) {
  return Number.isInteger(value) && value >= minimum && value <= maximum;
}

function isFiniteInRange(value: number, minimum: number, maximum: number) {
  return Number.isFinite(value) && value >= minimum && value <= maximum;
}

function isValidOAuthScopes(value: string) {
  if (value.trim().length === 0) return true;
  const scopes = normalizeOAuthScopes(value);
  return (
    scopes.length <= 20 &&
    scopes.every((scope) => scope.length >= 1 && scope.length <= 200) &&
    new Set(scopes).size === scopes.length
  );
}

function normalizeOAuthScopes(value: string) {
  return value
    .trim()
    .split(/\s+/u)
    .map((scope) => scope.trim())
    .filter(Boolean);
}

function normalize(values: Values) {
  const shared = {
    name: values.name.trim(),
    creditEndpoint: values.creditEndpoint.trim(),
    statusEndpoint: values.statusEndpoint.trim(),
    healthEndpoint: values.healthEndpoint.trim(),
    ...(values.transactionResultEndpoint.trim().length === 0
      ? {}
      : { transactionResultEndpoint: values.transactionResultEndpoint.trim() }),
    authType: values.authType,
    timeoutMilliseconds: values.timeoutMilliseconds,
    retryPolicy: {
      maximumAttempts: values.maximumAttempts,
      initialDelaySeconds: values.initialDelaySeconds,
      maximumDelaySeconds: values.maximumDelaySeconds,
      multiplier: values.multiplier,
      jitterPercent: values.jitterPercent,
    },
    secret: values.secret,
    reason: values.reason.trim(),
  };
  return values.authType === 'HMAC_API_KEY'
    ? { ...shared, hmacKeyId: values.hmacKeyId.trim() }
    : {
        ...shared,
        oauthTokenEndpoint: values.oauthTokenEndpoint.trim(),
        oauthClientId: values.oauthClientId.trim(),
        ...(values.oauthScopes.trim().length === 0
          ? {}
          : {
              oauthScopes: normalizeOAuthScopes(values.oauthScopes),
            }),
        ...(values.oauthAudience.trim().length === 0
          ? {}
          : { oauthAudience: values.oauthAudience.trim() }),
      };
}

function valuesFrom(configuration: PartnerCreditConfiguration): Values {
  const current = configuration.currentVersion?.configuration;
  if (current === undefined) return defaults;
  return {
    name: current.name,
    creditEndpoint: current.creditEndpoint,
    statusEndpoint: current.statusEndpoint,
    healthEndpoint: current.healthEndpoint,
    transactionResultEndpoint: current.transactionResultEndpoint ?? '',
    authType:
      current.authType === 'OAUTH2_CLIENT_CREDENTIALS'
        ? 'OAUTH2_CLIENT_CREDENTIALS'
        : 'HMAC_API_KEY',
    hmacKeyId: current.hmacKeyId ?? '',
    oauthTokenEndpoint: current.oauthTokenEndpoint ?? '',
    oauthClientId: current.oauthClientId ?? '',
    oauthScopes: current.oauthScopes?.join(' ') ?? '',
    oauthAudience: current.oauthAudience ?? '',
    timeoutMilliseconds: current.timeoutMilliseconds,
    maximumAttempts: current.retryPolicy.maximumAttempts,
    initialDelaySeconds: current.retryPolicy.initialDelaySeconds,
    maximumDelaySeconds: current.retryPolicy.maximumDelaySeconds,
    multiplier: current.retryPolicy.multiplier,
    jitterPercent: current.retryPolicy.jitterPercent,
    secret: '',
    reason: '',
  };
}

function safeError(cause: unknown, fallback: string): Pick<AppError, 'message' | 'requestId'> {
  if (typeof cause !== 'object' || cause === null) return { message: fallback, requestId: null };
  const candidate = cause as { readonly requestId?: unknown };
  return {
    message: fallback,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}

function SafeMutationError({ error }: { readonly error: Pick<AppError, 'message' | 'requestId'> }) {
  return (
    <Alert severity="error">
      {error.message}
      {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
    </Alert>
  );
}
